import type { SxProps, Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

const isDark = (theme: Theme) => theme.palette.mode === 'dark'

export const whatsNewBackdropSx: SxProps<Theme> = (theme) => ({
  backgroundColor: isDark(theme)
    ? alpha(theme.palette.common.black, 0.65)
    : 'rgba(17,25,39,.55)',
})

export const whatsNewPaperSx: SxProps<Theme> = (theme) => ({
  width: '100%',
  maxWidth: { xs: 'calc(100% - 32px)', sm: 860 },
  height: { xs: 'min(92vh, 720px)', sm: 'min(720px, 88vh)' },
  margin: { xs: 2, sm: 3 },
  borderRadius: 1,
  boxShadow: isDark(theme)
    ? '0 24px 60px rgba(0,0,0,.55)'
    : '0 24px 60px rgba(17,25,39,.3)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
})

export const whatsNewHeaderSx: SxProps<Theme> = (theme) => ({
  padding: '22px 28px 18px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.75,
  flexShrink: 0,
  background: isDark(theme)
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, transparent)`
    : 'linear-gradient(135deg, rgba(55,204,190,.08), rgba(55,204,190,0))',
})

export const whatsNewHeaderIconSx: SxProps<Theme> = (theme) => ({
  width: 40,
  height: 40,
  borderRadius: '10px',
  backgroundColor: alpha(theme.palette.primary.main, isDark(theme) ? 0.22 : 0.18),
  display: 'grid',
  placeItems: 'center',
  color: 'primary.main',
  flexShrink: 0,
})

export const whatsNewTitleSx: SxProps<Theme> = {
  fontSize: '17px',
  fontWeight: 700,
  color: 'text.primary',
  letterSpacing: '-.01em',
}

export const whatsNewSubtitleSx: SxProps<Theme> = {
  fontSize: '12px',
  color: 'text.secondary',
  marginTop: 0.375,
}

export const whatsNewCloseButtonSx: SxProps<Theme> = (theme) => ({
  minWidth: 0,
  width: 30,
  height: 30,
  borderRadius: 1,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: isDark(theme)
    ? theme.palette.neutral?.[800] ?? theme.palette.background.paper
    : theme.palette.common.white,
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
  color: 'text.secondary',
  padding: 0,
  flexShrink: 0,
  transition: 'all .15s ease',
  '&:hover': {
    backgroundColor: isDark(theme)
      ? theme.palette.neutral?.[700] ?? theme.palette.action.hover
      : theme.palette.neutral?.[100] ?? '#EEF1F4',
    color: 'text.primary',
  },
})

export const whatsNewBodySx: SxProps<Theme> = (theme) => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: isDark(theme)
    ? theme.palette.background.default
    : '#FAFBFC',
})

export const whatsNewEmptyTextSx: SxProps<Theme> = {
  fontSize: '13px',
  color: 'text.secondary',
  textAlign: 'center',
}

export const whatsNewFooterSx: SxProps<Theme> = (theme) => ({
  padding: '14px 28px',
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  backgroundColor: 'background.paper',
  flexShrink: 0,
  flexWrap: 'wrap',
})

export const whatsNewFooterTextSx: SxProps<Theme> = {
  fontSize: '11px',
  color: 'text.secondary',
}

export const whatsNewUnderstoodButtonSx: SxProps<Theme> = (theme) => ({
  backgroundColor: 'primary.main',
  color: theme.palette.primary.contrastText,
  border: 'none',
  padding: '8px 16px',
  borderRadius: 1,
  fontSize: '11px',
  fontWeight: 600,
  cursor: 'pointer',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'primary.dark',
  },
  '&.Mui-disabled': {
    backgroundColor: isDark(theme)
      ? theme.palette.neutral?.[700] ?? theme.palette.action.disabledBackground
      : theme.palette.neutral?.[200] ?? '#E5E9EE',
    color: isDark(theme)
      ? theme.palette.neutral?.[400] ?? theme.palette.text.disabled
      : '#A0A7B0',
  },
})

export const whatsNewCarouselHeaderSx: SxProps<Theme> = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  gap: 1.5,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'background.paper',
  flexShrink: 0,
})

export const whatsNewCarouselNavButtonSx =
  (disabled: boolean): SxProps<Theme> =>
  (theme) => ({
    minWidth: 0,
    width: 32,
    height: 32,
    borderRadius: 1,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: isDark(theme)
      ? theme.palette.neutral?.[800] ?? theme.palette.background.paper
      : theme.palette.common.white,
    display: 'grid',
    placeItems: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    color: disabled
      ? isDark(theme)
        ? theme.palette.neutral?.[500] ?? theme.palette.text.disabled
        : '#A0A7B0'
      : 'text.primary',
    opacity: disabled ? 0.5 : 1,
    padding: 0,
    transition: 'all .15s ease',
    flexShrink: 0,
    '&:hover:not(:disabled)': {
      backgroundColor: isDark(theme)
        ? theme.palette.neutral?.[700] ?? theme.palette.action.hover
        : theme.palette.neutral?.[100] ?? '#EEF1F4',
      borderColor: 'text.primary',
    },
  })

export const whatsNewCarouselPagerTextSx: SxProps<Theme> = {
  fontSize: '11px',
  color: 'text.secondary',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  whiteSpace: 'nowrap',
}

export const whatsNewCarouselPagerBoldSx: SxProps<Theme> = {
  fontWeight: 700,
  color: 'text.primary',
}

export const whatsNewCarouselDotSx =
  (active: boolean): SxProps<Theme> =>
  (theme) => ({
    minWidth: 0,
    width: active ? 18 : 6,
    minHeight: 0,
    height: 6,
    borderRadius: '3px',
    backgroundColor: active
      ? 'primary.main'
      : isDark(theme)
        ? theme.palette.neutral?.[600] ?? theme.palette.divider
        : theme.palette.neutral?.[200] ?? '#E5E9EE',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'all .2s ease',
    '&:hover': {
      backgroundColor: active
        ? 'primary.main'
        : isDark(theme)
          ? theme.palette.neutral?.[500] ?? theme.palette.action.hover
          : theme.palette.neutral?.[300] ?? '#D1D7DE',
    },
  })

export const whatsNewReleaseBlockSx: SxProps<Theme> = (theme) => ({
  backgroundColor: 'background.paper',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  overflow: 'hidden',
})

export const whatsNewReleaseHeaderSx: SxProps<Theme> = (theme) => ({
  backgroundColor: isDark(theme)
    ? theme.palette.neutral?.[800] ?? theme.palette.background.default
    : '#FAFBFC',
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '14px 20px',
  display: 'flex',
  alignItems: 'center',
  gap: 1.75,
})

export const whatsNewReleaseDateSx: SxProps<Theme> = {
  fontSize: '11px',
  fontWeight: 500,
  color: 'text.secondary',
  marginBottom: 0.25,
}

export const whatsNewReleaseTitleSx: SxProps<Theme> = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'text.primary',
  marginBottom: 0,
}

export const whatsNewReleaseSummarySx: SxProps<Theme> = {
  fontSize: '11px',
  color: 'text.secondary',
  // La console saisit résumé et descriptions dans un textarea : garder ses retours à la ligne.
  whiteSpace: 'pre-line',
}

export const whatsNewReleaseMetaSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.75,
  alignItems: 'center',
  fontSize: '10px',
  color: 'text.secondary',
}

export const whatsNewFeatureBadgeSx: SxProps<Theme> = (theme) => ({
  backgroundColor: alpha(theme.palette.primary.main, isDark(theme) ? 0.22 : 0.18),
  color: 'primary.main',
  padding: '3px 8px',
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '10px',
})

export const whatsNewFixBadgeSx: SxProps<Theme> = (theme) => ({
  backgroundColor: isDark(theme)
    ? alpha(theme.palette.warning.main, 0.18)
    : '#FFF4E6',
  color: isDark(theme) ? theme.palette.warning.light ?? '#FEDF89' : '#B25E00',
  padding: '3px 8px',
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '10px',
})

export const whatsNewReleaseContentSx: SxProps<Theme> = {
  padding: '18px 20px 20px',
  backgroundColor: 'background.paper',
}

export const whatsNewSectionTitleSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  marginBottom: 1.25,
  fontSize: '11px',
  fontWeight: 700,
  color: 'text.primary',
  textTransform: 'uppercase',
  letterSpacing: '.5px',
}

export const whatsNewChangelogItemSx =
  (isFeature: boolean): SxProps<Theme> =>
  (theme) => ({
    display: 'flex',
    gap: 1.5,
    p: '12px 14px',
    backgroundColor: isFeature
      ? alpha(theme.palette.primary.main, isDark(theme) ? 0.08 : 0.05)
      : isDark(theme)
        ? theme.palette.neutral?.[800] ?? theme.palette.background.default
        : '#FAFBFC',
    border: `1px solid ${
      isFeature
        ? alpha(theme.palette.primary.main, isDark(theme) ? 0.28 : 0.18)
        : theme.palette.divider
    }`,
    borderRadius: '9px',
  })

export const whatsNewChangelogIdSx =
  (isFeature: boolean): SxProps<Theme> =>
  (theme) => ({
    flexShrink: 0,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '.3px',
    color: isFeature
      ? isDark(theme)
        ? theme.palette.primary.light ?? theme.palette.primary.main
        : '#0FA89F'
      : 'text.secondary',
    backgroundColor: isFeature
      ? alpha(theme.palette.primary.main, isDark(theme) ? 0.18 : 0.12)
      : isDark(theme)
        ? theme.palette.neutral?.[700] ?? theme.palette.action.hover
        : theme.palette.neutral?.[100] ?? '#EEF1F4',
    padding: '3px 7px',
    borderRadius: '5px',
    height: 'fit-content',
    whiteSpace: 'nowrap',
  })

export const whatsNewChangelogTitleSx: SxProps<Theme> = {
  fontSize: '12px',
  color: 'text.primary',
  lineHeight: 1.5,
}

export const whatsNewChangelogDescriptionSx: SxProps<Theme> = {
  fontSize: '10px',
  color: 'text.secondary',
  marginTop: 0.5,
  lineHeight: 1.45,
  whiteSpace: 'pre-line',
}
